import React, { useState } from 'react';
import { ArrowLeft, KeyRound, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const PinInput = ({ id, value, onChange, testId, autoFocus }) => (
  <Input id={id} type="password" inputMode="numeric" autoComplete="off" maxLength={4} value={value} autoFocus={autoFocus}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
    className="h-14 text-center text-2xl font-mono tracking-[0.6em]" placeholder="••••" data-testid={testId} />
);

const Label = ({ children, htmlFor }) => <label htmlFor={htmlFor} className="block text-sm font-semibold text-[#1A1F2E] mb-1.5">{children}</label>;

/** Step 2 of the My Pay login: enter the PIN, or set one at the first login. */
export const PinCard = ({ t, mode, name, onSubmit, onBack, onEdit, busy, error }) => {
  const [pin, setPinS] = useState('');
  const [pin2, setPin2S] = useState('');
  const [local, setLocal] = useState('');
  const edit = (set) => (v) => { set(v); setLocal(''); onEdit(); };
  const setPin = edit(setPinS);
  const setPin2 = edit(setPin2S);
  const setup = mode === 'set_pin';
  const ready = pin.length === 4 && (!setup || pin2.length === 4) && !busy;
  const submit = (e) => {
    e.preventDefault();
    if (!ready) return;
    if (setup && pin !== pin2) { setLocal(t('pinMismatch')); return; }
    setLocal(''); onSubmit(pin);
  };
  const msg = local || error;
  return (
    <form onSubmit={submit} className="mypay-rise mx-auto w-full max-w-md rounded-2xl border border-[#E5DFD0] bg-white p-6 sm:p-8 shadow-[0_20px_60px_-30px_rgba(26,31,46,0.45)] space-y-5" data-testid={setup ? 'mypay-set-pin-form' : 'mypay-pin-form'}>
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-amber-500 text-white"><KeyRound className="h-5 w-5" /></span>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#2D5F4F]" data-testid="mypay-pin-hello">{t('pinHello', { name })}</p>
          <h3 className="text-lg font-bold text-[#1A1F2E]">{t(setup ? 'setPinTitle' : 'pinTitle')}</h3>
          <p className="text-sm text-gray-600 leading-snug">{t(setup ? 'setPinHint' : 'pinHint')}</p>
        </div>
      </div>
      <div><Label htmlFor="mypay-pin">{t(setup ? 'newPin' : 'pin')}</Label><PinInput id="mypay-pin" value={pin} onChange={setPin} testId="mypay-pin-input" autoFocus /></div>
      {setup && <div><Label htmlFor="mypay-pin2">{t('confirmPin')}</Label><PinInput id="mypay-pin2" value={pin2} onChange={setPin2} testId="mypay-pin-confirm-input" /></div>}
      {msg && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="mypay-pin-error">{msg}</p>}
      <Button type="submit" disabled={!ready} className="h-12 w-full rounded-xl bg-[#1A1F2E] text-base hover:bg-[#2D5F4F] transition-colors" data-testid="mypay-pin-submit">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t(setup ? 'savePin' : 'view')}
      </Button>
      <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 font-semibold text-[#2D5F4F] hover:underline" data-testid="mypay-pin-back"><ArrowLeft className="h-3.5 w-3.5" />{t('back')}</button>
        {!setup && <span data-testid="mypay-forgot-pin">{t('forgotPin')}</span>}
      </div>
    </form>
  );
};

export default PinCard;
